import { FormEvent, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { queryKeys } from '@/lib/queryKeys';
import {
  addUserLlmProviderModel,
  createUserLlmProvider,
  deleteUserLlmProvider,
  listUserLlmProviders,
  refreshUserLlmProviderModels,
  updateUserLlmProvider,
  updateUserLlmProviderModel,
  validateUserLlmProvider,
  type DiscoveredLlmModel,
  type UserLlmProviderRecord,
} from '@/services/templateSystemApi';
import { getUserFriendlyMessage } from '@/services/apiError';
import PageLoading from '@/components/common/PageLoading';
import ContentApiErrorState from '@/components/common/ContentErrorState';
import FormMessage from '@/components/common/FormMessage';
import EmptyState from '@/components/common/EmptyState';

interface ProviderDraft {
  name: string;
  baseUrl: string;
  apiKey: string;
}

const defaultProviderDraft: ProviderDraft = {
  name: '',
  baseUrl: '',
  apiKey: '',
};

interface LlmProvidersPageProps {
  embedded?: boolean;
}

export default function LlmProvidersPage({ embedded = false }: LlmProvidersPageProps) {
  const queryClient = useQueryClient();
  const providersQuery = useQuery({
    queryKey: queryKeys.llmProviders.list,
    queryFn: () => listUserLlmProviders(),
    retry: 1,
  });

  const [draft, setDraft] = useState<ProviderDraft>(defaultProviderDraft);
  const [creationError, setCreationError] = useState('');
  const [validationResult, setValidationResult] = useState<{
    status: 'unknown' | 'ok' | 'failed';
    listingSupported: boolean;
    error?: string;
    normalizedBaseUrl: string;
    discoveredModels: DiscoveredLlmModel[];
  } | null>(null);
  const [selectedModelIds, setSelectedModelIds] = useState<string[]>([]);
  const [manualModel, setManualModel] = useState('');
  const [editingProviderId, setEditingProviderId] = useState<string>('');
  const [editDraft, setEditDraft] = useState<{
    name: string;
    baseUrl: string;
    apiKey: string;
    enabled: boolean;
  }>({
    name: '',
    baseUrl: '',
    apiKey: '',
    enabled: true,
  });
  const [manualModelByProvider, setManualModelByProvider] = useState<Record<string, string>>({});

  const validateMutation = useMutation({
    mutationFn: () =>
      validateUserLlmProvider({
        providerType: 'openai_compatible',
        name: draft.name,
        baseUrl: draft.baseUrl,
        apiKey: draft.apiKey,
      }),
    onSuccess: data => {
      setValidationResult(data);
      setSelectedModelIds(data.discoveredModels.map(model => model.modelId));
    },
    onError: error => {
      setValidationResult(null);
      setCreationError(getUserFriendlyMessage(error, '验证失败'));
    },
  });

  const createMutation = useMutation({
    mutationFn: (params: {
      importModels: boolean;
      selectedModels: DiscoveredLlmModel[];
      normalizedBaseUrl?: string;
    }) =>
      createUserLlmProvider({
        providerType: 'openai_compatible',
        name: draft.name,
        baseUrl: params.normalizedBaseUrl || draft.baseUrl,
        apiKey: draft.apiKey,
        enabled: true,
        isDefault: true,
        importModels: params.importModels,
        selectedModels: params.selectedModels,
      }),
    onSuccess: () => {
      setDraft(defaultProviderDraft);
      setValidationResult(null);
      setSelectedModelIds([]);
      setManualModel('');
      setCreationError('');
      void queryClient.invalidateQueries({ queryKey: queryKeys.llmProviders.list });
    },
    onError: error => {
      setCreationError(getUserFriendlyMessage(error, '保存 provider 失败'));
    },
  });

  const refreshMutation = useMutation({
    mutationFn: (providerId: string) => refreshUserLlmProviderModels(providerId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.llmProviders.list });
    },
  });

  const updateProviderMutation = useMutation({
    mutationFn: (params: {
      providerId: string;
      payload: {
        name?: string;
        baseUrl?: string;
        apiKey?: string;
        enabled?: boolean;
        isDefault?: boolean;
      };
    }) => updateUserLlmProvider(params.providerId, params.payload),
    onSuccess: () => {
      setEditingProviderId('');
      setEditDraft({
        name: '',
        baseUrl: '',
        apiKey: '',
        enabled: true,
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.llmProviders.list });
    },
  });

  const deleteProviderMutation = useMutation({
    mutationFn: (providerId: string) => deleteUserLlmProvider(providerId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.llmProviders.list });
    },
  });

  const updateModelMutation = useMutation({
    mutationFn: (params: {
      providerId: string;
      modelId: string;
      payload: { isEnabled?: boolean; isDefault?: boolean };
    }) => updateUserLlmProviderModel(params.providerId, params.modelId, params.payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.llmProviders.list });
    },
  });

  const addModelMutation = useMutation({
    mutationFn: (params: { providerId: string; modelId: string }) =>
      addUserLlmProviderModel(params.providerId, {
        modelId: params.modelId,
        label: params.modelId,
        isEnabled: true,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.llmProviders.list });
    },
  });

  const selectedModels = useMemo(() => {
    if (!validationResult) {
      return [];
    }
    return validationResult.discoveredModels.filter(model =>
      selectedModelIds.includes(model.modelId)
    );
  }, [selectedModelIds, validationResult]);

  if (providersQuery.isLoading) {
    return <PageLoading message="Provider 列表加载中…" />;
  }

  if (providersQuery.isError) {
    return (
      <div className={embedded ? '' : 'max-w-[1180px] mx-auto px-6 py-12'}>
        <ContentApiErrorState
          error={providersQuery.error}
          onRetry={() => void providersQuery.refetch()}
        />
      </div>
    );
  }

  const providers = providersQuery.data ?? [];

  return (
    <div className={embedded ? 'space-y-6' : 'max-w-[1180px] mx-auto px-6 py-12 space-y-6'}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-semibold text-apple-near-black text-[2rem] leading-tight tracking-tight">
            LLM Providers
          </h1>
          <p className="text-apple-gray text-[0.9375rem] mt-1">
            配置你自己的 OpenAI-compatible provider 和模型，用于模板真实生成。
          </p>
        </div>
        {!embedded ? (
          <Link to="/templates" className="btn btn-secondary">
            返回模板库
          </Link>
        ) : null}
      </div>

      <section className="bg-white rounded-2xl p-5 shadow-card-sm space-y-4">
        <h2 className="font-semibold text-[1.0625rem] text-apple-near-black">新增 Provider</h2>
        {creationError ? <FormMessage tone="error" message={creationError} /> : null}
        <form
          className="grid grid-cols-1 md:grid-cols-2 gap-3"
          onSubmit={event => {
            event.preventDefault();
            setCreationError('');
            void validateMutation.mutateAsync();
          }}
        >
          <div>
            <label className="text-[0.75rem] text-apple-gray mb-1 block">Provider Name</label>
            <input
              className="input"
              placeholder="My OpenAI-Compatible"
              value={draft.name}
              onChange={event => setDraft(prev => ({ ...prev, name: event.target.value }))}
            />
          </div>
          <div>
            <label className="text-[0.75rem] text-apple-gray mb-1 block">Provider Type</label>
            <input className="input bg-apple-light-gray" value="openai_compatible" disabled />
          </div>
          <div className="md:col-span-2">
            <label className="text-[0.75rem] text-apple-gray mb-1 block">Base URL</label>
            <input
              className="input"
              placeholder="https://api.openai.com/v1"
              value={draft.baseUrl}
              onChange={event => setDraft(prev => ({ ...prev, baseUrl: event.target.value }))}
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-[0.75rem] text-apple-gray mb-1 block">API Key</label>
            <input
              className="input"
              type="password"
              placeholder="sk-..."
              value={draft.apiKey}
              onChange={event => setDraft(prev => ({ ...prev, apiKey: event.target.value }))}
            />
          </div>
          <div className="md:col-span-2 flex flex-wrap gap-2">
            <button
              className="btn btn-primary"
              type="submit"
              disabled={
                validateMutation.isPending ||
                !draft.name.trim() ||
                !draft.baseUrl.trim() ||
                !draft.apiKey.trim()
              }
            >
              {validateMutation.isPending ? '验证中…' : 'Validate & Detect Models'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={
                createMutation.isPending ||
                !draft.name.trim() ||
                !draft.baseUrl.trim() ||
                !draft.apiKey.trim()
              }
              onClick={() => {
                void createMutation.mutateAsync({
                  importModels: false,
                  selectedModels: [],
                  normalizedBaseUrl: validationResult?.normalizedBaseUrl,
                });
              }}
            >
              保存 Provider（不导入模型）
            </button>
          </div>
        </form>

        {validationResult ? (
          <div className="border border-apple-border rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[0.875rem] text-apple-near-black">
                验证状态:
                <span className="ml-2 font-medium">
                  {validationResult.status === 'ok'
                    ? '成功'
                    : validationResult.status === 'failed'
                      ? '失败'
                      : '部分可用（不支持 /models）'}
                </span>
              </p>
              <p className="text-[0.75rem] text-apple-gray">
                规范化 Base URL: {validationResult.normalizedBaseUrl}
              </p>
            </div>
            {validationResult.error ? (
              <FormMessage
                tone={validationResult.status === 'failed' ? 'error' : 'info'}
                message={validationResult.error}
              />
            ) : null}

            {validationResult.discoveredModels.length > 0 ? (
              <div className="space-y-2">
                <p className="text-[0.8125rem] text-apple-gray">
                  检测到的模型（可取消勾选后再导入）
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[220px] overflow-auto">
                  {validationResult.discoveredModels.map(model => (
                    <label
                      key={model.modelId}
                      className="border border-apple-border rounded-lg px-3 py-2 flex items-center gap-2"
                    >
                      <input
                        type="checkbox"
                        checked={selectedModelIds.includes(model.modelId)}
                        onChange={event => {
                          setSelectedModelIds(prev => {
                            if (event.target.checked) {
                              return Array.from(new Set([...prev, model.modelId]));
                            }
                            return prev.filter(id => id !== model.modelId);
                          });
                        }}
                      />
                      <span className="text-[0.875rem] text-apple-near-black">{model.modelId}</span>
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-[0.8125rem] text-apple-gray">
                未发现可自动导入模型。你仍然可以先保存 provider，再手动添加模型。
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="btn btn-primary"
                disabled={createMutation.isPending}
                onClick={() => {
                  void createMutation.mutateAsync({
                    importModels: selectedModels.length > 0,
                    selectedModels,
                    normalizedBaseUrl: validationResult.normalizedBaseUrl,
                  });
                }}
              >
                {createMutation.isPending ? '保存中…' : '保存 Provider 并导入已选模型'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={createMutation.isPending}
                onClick={() => {
                  const manual = manualModel.trim();
                  if (!manual) {
                    setCreationError('请填写手动模型 ID');
                    return;
                  }
                  void createMutation.mutateAsync({
                    importModels: true,
                    selectedModels: [{ modelId: manual, label: manual }],
                    normalizedBaseUrl: validationResult.normalizedBaseUrl,
                  });
                }}
              >
                保存并导入手动模型
              </button>
              <input
                className="input max-w-[260px]"
                placeholder="manual-model-id"
                value={manualModel}
                onChange={event => setManualModel(event.target.value)}
              />
            </div>
          </div>
        ) : null}
      </section>

      {providers.length === 0 ? (
        <EmptyState
          title="还没有 Provider"
          description="先在上方添加一个 OpenAI-compatible provider。"
        />
      ) : (
        <div className="space-y-4">
          {providers.map(provider => (
            <ProviderCard
              key={provider.id}
              provider={provider}
              isEditing={editingProviderId === provider.id}
              editDraft={editDraft}
              onStartEdit={() => {
                setEditingProviderId(provider.id);
                setEditDraft({
                  name: provider.name,
                  baseUrl: provider.baseUrl,
                  apiKey: '',
                  enabled: provider.enabled,
                });
              }}
              onCancelEdit={() => {
                setEditingProviderId('');
              }}
              onEditDraftChange={setEditDraft}
              onSaveEdit={() => {
                void updateProviderMutation.mutateAsync({
                  providerId: provider.id,
                  payload: {
                    name: editDraft.name,
                    baseUrl: editDraft.baseUrl,
                    apiKey: editDraft.apiKey.trim() || undefined,
                    enabled: editDraft.enabled,
                  },
                });
              }}
              onSetDefault={() => {
                void updateProviderMutation.mutateAsync({
                  providerId: provider.id,
                  payload: { isDefault: true },
                });
              }}
              onRefreshModels={() => {
                void refreshMutation.mutateAsync(provider.id);
              }}
              onDelete={() => {
                if (!window.confirm(`确认删除 provider “${provider.name}”？`)) {
                  return;
                }
                void deleteProviderMutation.mutateAsync(provider.id);
              }}
              onToggleModel={(modelId, isEnabled) => {
                void updateModelMutation.mutateAsync({
                  providerId: provider.id,
                  modelId,
                  payload: { isEnabled },
                });
              }}
              onSetDefaultModel={modelId => {
                void updateModelMutation.mutateAsync({
                  providerId: provider.id,
                  modelId,
                  payload: { isDefault: true },
                });
              }}
              manualModelValue={manualModelByProvider[provider.id] ?? ''}
              onManualModelValueChange={next => {
                setManualModelByProvider(prev => ({ ...prev, [provider.id]: next }));
              }}
              onAddManualModel={() => {
                const manual = (manualModelByProvider[provider.id] ?? '').trim();
                if (!manual) {
                  return;
                }
                void addModelMutation.mutateAsync(
                  { providerId: provider.id, modelId: manual },
                  {
                    onSuccess: () => {
                      setManualModelByProvider(prev => ({ ...prev, [provider.id]: '' }));
                    },
                  }
                );
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface ProviderCardProps {
  provider: UserLlmProviderRecord;
  isEditing: boolean;
  editDraft: { name: string; baseUrl: string; apiKey: string; enabled: boolean };
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onEditDraftChange: (next: {
    name: string;
    baseUrl: string;
    apiKey: string;
    enabled: boolean;
  }) => void;
  onSaveEdit: () => void;
  onSetDefault: () => void;
  onRefreshModels: () => void;
  onDelete: () => void;
  onToggleModel: (modelId: string, enabled: boolean) => void;
  onSetDefaultModel: (modelId: string) => void;
  manualModelValue: string;
  onManualModelValueChange: (next: string) => void;
  onAddManualModel: () => void;
}

function ProviderCard(props: ProviderCardProps) {
  const { provider } = props;
  return (
    <section className="bg-white rounded-2xl p-5 shadow-card-sm space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold text-apple-near-black text-[1.0625rem]">{provider.name}</h2>
          <p className="text-apple-gray text-[0.8125rem] mt-1">
            {provider.baseUrl} · {provider.apiKeyMasked}
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            <span className="text-[0.75rem] px-2 py-1 rounded-full bg-apple-light-gray text-apple-gray">
              type: {provider.providerType}
            </span>
            <span className="text-[0.75rem] px-2 py-1 rounded-full bg-apple-light-gray text-apple-gray">
              validation: {provider.lastValidationStatus}
            </span>
            {provider.isDefault ? (
              <span className="text-[0.75rem] px-2 py-1 rounded-full bg-[#e8f4ff] text-[#0071e3]">
                默认 provider
              </span>
            ) : null}
            {provider.enabled ? (
              <span className="text-[0.75rem] px-2 py-1 rounded-full bg-[#edf7ed] text-[#138a36]">
                已启用
              </span>
            ) : (
              <span className="text-[0.75rem] px-2 py-1 rounded-full bg-[#fff1f0] text-[#c2410c]">
                已禁用
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="btn btn-secondary" onClick={props.onRefreshModels}>
            刷新模型
          </button>
          {!provider.isDefault ? (
            <button className="btn btn-secondary" onClick={props.onSetDefault}>
              设为默认
            </button>
          ) : null}
          {props.isEditing ? (
            <>
              <button className="btn btn-primary" onClick={props.onSaveEdit}>
                保存
              </button>
              <button className="btn btn-secondary" onClick={props.onCancelEdit}>
                取消
              </button>
            </>
          ) : (
            <button className="btn btn-secondary" onClick={props.onStartEdit}>
              编辑
            </button>
          )}
          <button className="btn-danger" onClick={props.onDelete}>
            删除
          </button>
        </div>
      </div>

      {props.isEditing ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border border-apple-border rounded-xl p-3">
          <input
            className="input"
            placeholder="Provider Name"
            value={props.editDraft.name}
            onChange={event =>
              props.onEditDraftChange({ ...props.editDraft, name: event.target.value })
            }
          />
          <input
            className="input"
            placeholder="Base URL"
            value={props.editDraft.baseUrl}
            onChange={event =>
              props.onEditDraftChange({ ...props.editDraft, baseUrl: event.target.value })
            }
          />
          <input
            className="input md:col-span-2"
            placeholder="Replace API key (optional)"
            type="password"
            value={props.editDraft.apiKey}
            onChange={event =>
              props.onEditDraftChange({ ...props.editDraft, apiKey: event.target.value })
            }
          />
          <label className="inline-flex items-center gap-2 text-[0.875rem] text-apple-gray">
            <input
              type="checkbox"
              checked={props.editDraft.enabled}
              onChange={event =>
                props.onEditDraftChange({ ...props.editDraft, enabled: event.target.checked })
              }
            />
            启用 provider
          </label>
        </div>
      ) : null}

      <div className="border border-apple-border rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-medium text-[0.9375rem] text-apple-near-black">模型管理</h3>
          <span className="text-[0.75rem] text-apple-gray">{provider.models.length} models</span>
        </div>

        {provider.models.length === 0 ? (
          <p className="text-[0.8125rem] text-apple-gray">
            尚未导入模型，可手动添加或使用“刷新模型”。
          </p>
        ) : (
          <div className="space-y-2">
            {provider.models.map(model => (
              <div
                key={model.id}
                className="flex flex-wrap items-center justify-between gap-2 border border-apple-border rounded-lg p-2"
              >
                <div>
                  <p className="text-[0.875rem] text-apple-near-black">{model.modelId}</p>
                  <p className="text-[0.75rem] text-apple-gray">{model.label}</p>
                </div>
                <div className="flex items-center gap-2">
                  <label className="inline-flex items-center gap-1 text-[0.75rem] text-apple-gray">
                    <input
                      type="checkbox"
                      checked={model.isEnabled}
                      onChange={event => props.onToggleModel(model.id, event.target.checked)}
                    />
                    启用
                  </label>
                  {!model.isDefault ? (
                    <button
                      className="btn btn-secondary"
                      onClick={() => props.onSetDefaultModel(model.id)}
                    >
                      设默认
                    </button>
                  ) : (
                    <span className="text-[0.75rem] px-2 py-1 rounded-full bg-[#e8f4ff] text-[#0071e3]">
                      默认模型
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <form
          className="flex flex-wrap gap-2"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            props.onAddManualModel();
          }}
        >
          <input
            className="input max-w-[260px]"
            placeholder="manual-model-id"
            value={props.manualModelValue}
            onChange={event => props.onManualModelValueChange(event.target.value)}
          />
          <button className="btn btn-secondary" type="submit">
            手动添加模型
          </button>
        </form>
      </div>
    </section>
  );
}
